import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DomainAvailability } from '../../services/domainService';
import { useAuth } from '../../contexts/AuthContext';

interface DomainSearchProps {
  onDomainSelect?: (domain: DomainAvailability) => void;
  initialSearch?: string;
}

const DomainSearch: React.FC<DomainSearchProps> = ({ onDomainSelect, initialSearch = '' }) => {
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [error, setError] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();
  const selectedTlds = ['.com', '.net', '.org', '.io', '.co', '.app', '.dev'];

  useEffect(() => {
    if (initialSearch) {
      setSearchTerm(initialSearch);
      performSearch(initialSearch);
    }
  }, [initialSearch]); // performSearch is defined inline and doesn't need to be in deps

  // A single bare label: letters/digits/hyphens, no leading/trailing hyphen.
  // Used when the user just typed "sivra" with no TLD — we'll search across
  // the default TLD set on the results page.
  const isValidBareLabel = (term: string) => {
    return /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?$/.test(term);
  };

  // A full domain like "sivra.click" — one or more labels separated by dots,
  // each label valid, total ≤253 chars, TLD ≥2 letters. Doesn't care WHICH
  // TLD; works for any AWS-supported one.
  const isValidFullDomain = (term: string) => {
    return /^(?=.{1,253}$)([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(term);
  };

  const performSearch = async (searchTermParam?: string) => {
    const termToSearch = searchTermParam || searchTerm;
    const cleanTerm = termToSearch.trim().toLowerCase();

    if (!cleanTerm) {
      setError('Please enter a domain name');
      return;
    }

    // Check if user is authenticated
    if (!user && !localStorage.getItem('token')) {
      setShowLoginPrompt(true);
      return;
    }

    // Two valid input shapes:
    //   1) Bare label ("sivra") → check across default TLDs on results page.
    //   2) Full domain ("sivra.click") → check just that one domain.
    if (cleanTerm.includes('.')) {
      if (!isValidFullDomain(cleanTerm)) {
        setError('Please enter a valid domain name (e.g. example.com or example.click).');
        return;
      }
    } else {
      if (!isValidBareLabel(cleanTerm)) {
        setError('Please enter a valid name using only letters, numbers, and hyphens (no spaces or special characters).');
        return;
      }
    }

    setError('');
    navigate(`/domain-search/${encodeURIComponent(cleanTerm)}`);
  };

  const handleSearch = async () => {
    await performSearch();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  const handleLoginRedirect = () => {
    const currentUrl = window.location.pathname + window.location.search;
    const returnUrl = encodeURIComponent(currentUrl);
    navigate(`/login?return=${returnUrl}&message=Please sign in to search for domains`);
  };

  const LoginPrompt = () => (
    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
      <div className="relative z-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 via-purple-500 to-blue-600 rounded-2xl mb-6 shadow-lg relative overflow-hidden mx-auto">
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
          <svg className="w-8 h-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Required</h2>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          To search for domain availability, you'll need a 12and22 account.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleLoginRedirect}
            className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Sign In to Search</span>
          </button>
          
          <button
            onClick={() => navigate('/register', { 
              state: { returnUrl: window.location.pathname + window.location.search }
            })}
            className="border-2 border-blue-400 text-blue-600 px-8 py-3 rounded-lg font-semibold text-lg hover:bg-gradient-to-br hover:from-blue-400 hover:via-purple-500 hover:to-blue-600 hover:text-white hover:border-transparent transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent group-hover:from-white/20 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Create Account</span>
          </button>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Why do I need an account?</h4>
          <ul className="text-sm text-blue-800 space-y-1 text-left">
            <li>• Access real-time domain availability</li>
            <li>• Get accurate pricing and instant domain registration</li>
            <li>• Manage your domains with advanced DNS tools</li>
            <li>• Receive priority customer support</li>
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-primary-navy mb-2">Find Your Perfect Domain</h2>
        <p className="text-gray-600">Search for available domain names.</p>
      </div>

      {showLoginPrompt ? (
        <LoginPrompt />
      ) : (
        <>
          <form onSubmit={handleSubmit} className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Enter domain name (e.g., mybusiness, test123)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                />
              </div>
              <button
                type="submit"
                className="bg-secondary-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-secondary-600 transition-colors duration-200"
              >
                Search
              </button>
            </div>
          </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default DomainSearch;